import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { genSeed, makeRng, pickN } from '../game/rng';
import { newPlayer, startCombat, playCard, endTurn, generateCardReward } from '../game/engine';
import { generateMap, NODE_TYPES, MAP_LENGTH } from '../game/map';
import { pickEnemyForFloor, pickEliteForFloor, getMidBoss, getFinalBoss, ENEMIES } from '../game/enemies';
import { CARDS, canUpgrade, upgradedId, upgradeBranches, CARD_POOL_RARE, CARD_POOL_UNCOMMON, CARD_POOL_DIVINE, CARD_POOL_DIVINE_CLASS, isDivineCard } from '../game/cards';
import { RELICS, ALL_RELIC_IDS } from '../game/relics';
import { classById } from '../game/classes';
import { sfx, startMusic, stopMusic, setMuted, setMusicAct, startMenuMusic, stopMenuMusic } from '../game/sound';
import { ASCENSIONS, ascById } from '../game/ascensions';
import { CURSES, curseById, mergeCurseModifiers, stackedRunTokenMult } from '../game/curses';
import { loadGhost, saveGhostIfBest } from '../game/ghost';
import { ACHIEVEMENTS } from '../game/achievements';
import { fetchProfile, patchProfile, fetchCurse, fetchCurseStack, createCurse, fetchSeedGhosts, currentWeekKey, normalizeTag } from '../game/profile';
import { partyUpdate as partyUpdateApi } from '../game/party';
import { bumpCardStat } from '../game/cardStats';
import { applyPerks } from '../game/perks';
import { pickBoons, SHRINES, TREASURE_OUTCOMES, pickWeightedChest } from '../game/boons';
import { BLESSINGS, rollBlessingOffers } from '../game/blessings';
import { mutatorForWeek, rollCombatModifier, COMBAT_MODIFIERS, activeRelicSets } from '../game/depth';
import { connectAnonymousChat, disconnectChat as disconnectTwitchChat } from '../game/twitch';

const GameContext = createContext(null);
export const useGame = () => useContext(GameContext);

const LOCAL_TAG_KEY = 'friendslop.tag';

export function GameProvider({ children }) {
  const [screen, setScreen] = useState('menu');
  // v3.13 optional deep-link tab for the Leaderboard screen (e.g. 'cursed')
  const [leaderboardInitialTab, setLeaderboardInitialTab] = useState(null);

  // v1.5: Play menu ambient on non-combat screens; combat music takes over during fights
  useEffect(() => {
    const combatLike = screen === 'combat';
    if (combatLike) { stopMenuMusic(); }
    else if (screen === 'menu' || screen === 'map' || screen === 'shop' || screen === 'codex' || screen === 'boon' || screen === 'shrine') {
      startMenuMusic();
    }
    return () => { stopMenuMusic(); };
  }, [screen]);
  const [playerName, setPlayerNameState] = useState('');
  const [profile, setProfile] = useState(null); // server-side profile
  const [selectedClass, setSelectedClass] = useState(null);
  const [pendingClassChoice, setPendingClassChoice] = useState(null);
  const [selectedAscension, setSelectedAscension] = useState(0);
  const [activeCurse, setActiveCurse] = useState(null); // {curse_id, cursed_by, run_code, original_score, stack: [{curse_id,cursed_by,...}]}
  const [activeCurseStack, setActiveCurseStack] = useState([]); // v3.10: full stack of curses on this run code
  // v3.14 GHOST MODE: previous best run for the current seed (if any). null when no prior data.
  const [ghostRun, setGhostRun] = useState(null);
  const [run, setRun] = useState(null);
  const [combat, setCombat] = useState(null);
  const [pendingReward, setPendingReward] = useState(null);
  const [pendingBlessing, setPendingBlessing] = useState(null); // v2.1
  const [pendingEliteChoice, setPendingEliteChoice] = useState(null); // v2.2
  const [pendingRecap, setPendingRecap] = useState(null); // v2.4 post-combat pile summary
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState(0); // v2.4 auto-save toast
  const [pendingShop, setPendingShop] = useState(null);
  const [pendingEvent, setPendingEvent] = useState(null);
  const [pendingRest, setPendingRest] = useState(null);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [pendingBoon, setPendingBoon] = useState(null);
  const [pendingShrine, setPendingShrine] = useState(null);
  const [pendingTreasure, setPendingTreasure] = useState(null);
  const [pendingBranchPick, setPendingBranchPick] = useState(null);
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const [floatingText, setFloatingText] = useState('');
  const [muted, setMutedState] = useState(false);
  const [streamerMode, setStreamerMode] = useState(() => {
    try { return localStorage.getItem('friendslop.streamer') === '1'; } catch (e) { return false; }
  });
  // v0.9.5 Twitch chat integration
  const [twitchChannel, setTwitchChannel] = useState(() => {
    try { return localStorage.getItem('friendslop.twitchChannel') || ''; } catch (e) { return ''; }
  });
  const [twitchStatus, setTwitchStatus] = useState({ connected: false, channel: null });
  const [chatVotes, setChatVotes] = useState({});   // choice_index -> Set of voters
  const [chatVoteWinner, setChatVoteWinner] = useState(null);
  const [draftDeck, setDraftDeck] = useState(null);
  const [partyState, setPartyState] = useState(null);
  const [achievementToasts, setAchievementToasts] = useState([]);
  const [seedGhosts, setSeedGhosts] = useState([]);
  const [runAchievements, setRunAchievements] = useState([]);
  const runAchievementsAffixes = useRef([]);
  // v1.1 COLORPILL: run streak (consecutive victories, persisted locally)
  const [runStreak, setRunStreak] = useState(() => {
    try { return Number(localStorage.getItem('friendslop.streak') || 0); } catch (e) { return 0; }
  });
  const [bestStreak, setBestStreak] = useState(() => {
    try { return Number(localStorage.getItem('friendslop.bestStreak') || 0); } catch (e) { return 0; }
  });
  const bumpStreak = useCallback((victory) => {
    setRunStreak((s) => {
      const next = victory ? s + 1 : 0;
      try { localStorage.setItem('friendslop.streak', String(next)); } catch (e) {}
      setBestStreak((b) => {
        const nb = Math.max(b, next);
        try { localStorage.setItem('friendslop.bestStreak', String(nb)); } catch (e) {}
        return nb;
      });
      return next;
    });
  }, []);

  // Restore local tag from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_TAG_KEY);
    if (saved) {
      setPlayerNameState(saved);
      fetchProfile(saved).then(p => { if (p) setProfile(p); });
    }
  }, []);

  const setPlayerName = useCallback((name) => {
    const norm = normalizeTag(name);
    setPlayerNameState(name);
    if (norm) localStorage.setItem(LOCAL_TAG_KEY, name);
  }, []);

  // v2.0 QoL: debounced auto-fetch profile when the tag input changes so the
  // menu avatar chip live-refreshes without a page reload.
  useEffect(() => {
    const tag = normalizeTag(playerName);
    if (!tag) return;
    const t = setTimeout(() => {
      fetchProfile(tag).then(p => { if (p) setProfile(p); });
    }, 450);
    return () => clearTimeout(t);
  }, [playerName]);

  const refreshProfile = useCallback(async () => {
    const tag = normalizeTag(playerName);
    if (!tag) return null;
    const p = await fetchProfile(tag);
    if (p) setProfile(p);
    return p;
  }, [playerName]);

  useEffect(() => {
    if (!floatingText) return;
    const t = setTimeout(() => setFloatingText(''), 1500);
    return () => clearTimeout(t);
  }, [floatingText]);

  const pushAchievement = useCallback((id) => {
    if (runAchievements.includes(id)) return;
    // v3.20 FIX: also dedupe against the PERSISTENT profile.achievements list. Prior behavior
    // reset per-run so every time a player triggered `first_blood` / `hoarder` / etc. in a new run
    // it re-fired the toast + Steam call + token grant. Now, an already-earned achievement is
    // silently marked as "seen this run" (no toast, no Steam, no tokens) so the run-end patch
    // still receives the id but won't double-credit on the backend either.
    const alreadyOwned = (profile?.achievements || []).includes(id);
    setRunAchievements(prev => prev.includes(id) ? prev : [...prev, id]);
    if (alreadyOwned) return;
    const a = ACHIEVEMENTS[id];
    if (!a) return;
    const tid = Math.random().toString(36).slice(2);
    setAchievementToasts(prev => [...prev, { id: tid, achievement: a }]);
    sfx.levelup();
    // v3.17 STEAM: forward to Steam via /app/frontend/src/game/steam.js bridge (offline-safe).
    try {
      import('../game/steam').then(m => m.unlockAchievement && m.unlockAchievement(id)).catch(() => {});
    } catch (e) {}
    // Legacy v1.6 window.friendslop bridge (kept for backwards compat with older Electron builds).
    try {
      if (typeof window !== 'undefined' && window.friendslop && window.friendslop.setAchievement) {
        window.friendslop.setAchievement(id);
      }
    } catch (e) {}
    setTimeout(() => {
      setAchievementToasts(prev => prev.filter(t => t.id !== tid));
    }, 4200);
  }, [runAchievements, profile]);

  const beginClassSelect = useCallback(async (seed, daily = null, runCode = null, weekly = false) => {
    const s = (seed || genSeed()).toUpperCase();
    // v3.10 STACKED CURSES: load the entire stack (in addition to the legacy single-latest fetch).
    let curse = null;
    let stack = [];
    if (runCode) {
      try {
        const stackRes = await fetchCurseStack(runCode);
        stack = stackRes?.curses || [];
        curse = stack.length > 0 ? { ...stack[stack.length - 1], stack } : null;
      } catch (e) {
        // fallback to legacy single-curse endpoint
        try { curse = await fetchCurse(runCode); if (curse) stack = [curse]; } catch (e2) {}
      }
    }
    setActiveCurse(curse);
    setActiveCurseStack(stack);
    setPendingClassChoice({ seed: s, daily, runCode, weekly });
    setScreen('classselect');
  }, []);

  // v1.0: Combat History — record a floor event on the current run
  const recordFloorEvent = useCallback((event) => {
    setRun(r => r ? { ...r, history: [...(r.history || []), { ...event, ts: Date.now() }] } : r);
  }, []);

  // v1.1 COLORPILL: color-burst helper (confetti/particles/screen-flash)
  // MUST be declared before callbacks that reference it (handleCombatEnd, takeRewardCard) to avoid TDZ.
  const triggerBurst = useCallback((kind = 'pink') => {
    try {
      const el = document.createElement('div');
      el.className = `color-burst ${kind}`;
      // Spawn a bunch of particles inside the burst container
      const count = kind === 'rainbow' ? 60 : kind === 'gold' ? 36 : kind === 'shatter' ? 44 : 24;
      const palette = {
        rainbow: ['#ff2ea6', '#00e5ff', '#ffe74a', '#a0ff2e', '#b46bff', '#ff5b3d'],
        gold:    ['#ffe74a', '#ffb347', '#ff8c1a', '#fff2a8'],
        pink:    ['#ff2ea6', '#ff8fd1', '#ffc0e2'],
        shatter: ['#ff3355', '#5b0011', '#8a0022', '#ffffff'],
        streak:  ['#00e5ff', '#7cfffb', '#ffffff', '#a0ff2e'],
      }[kind] || ['#ff2ea6'];
      for (let i = 0; i < count; i++) {
        const p = document.createElement('span');
        p.className = 'color-burst-piece';
        const c = palette[i % palette.length];
        p.style.background = c;
        p.style.setProperty('--dx', (Math.random() * 2 - 1).toFixed(3));
        p.style.setProperty('--dy', (Math.random() * -1.4 - 0.2).toFixed(3));
        p.style.setProperty('--r', (Math.random() * 720 - 360).toFixed(1) + 'deg');
        p.style.setProperty('--d', (Math.random() * 0.35).toFixed(2) + 's');
        p.style.left = (48 + Math.random() * 4) + '%';
        p.style.top  = (48 + Math.random() * 4) + '%';
        el.appendChild(p);
      }
      document.body.appendChild(el);
      setTimeout(() => el.remove(), kind === 'rainbow' ? 1600 : kind === 'shatter' ? 1400 : 1100);
    } catch (e) {}
  }, []);

  const confirmClass = useCallback(async (classId, ascensionLevel = 0, useAltStarter = false) => {
    if (!pendingClassChoice) return;
    const seed = pendingClassChoice.seed.toUpperCase();
    const useDraft = !!pendingClassChoice.useDraft;
    const effectiveClass = pendingClassChoice.forcedClassId || classId;
    const classDef = classById(effectiveClass);
    const asc = ascById(ascensionLevel);
    // v3.10 STACKED CURSES: merge modifiers from the entire stack (compound multipliers, sum deltas, min for start-gold).
    const curseIdsForStack = (activeCurseStack && activeCurseStack.length > 0)
      ? activeCurseStack.map(c => c.curse_id)
      : (activeCurse ? [activeCurse.curse_id] : []);
    const curseMods = curseIdsForStack.length > 0 ? mergeCurseModifiers(curseIdsForStack) : {};
    const mastery = profile?.masteries?.[effectiveClass] || 0;
    // v0.9.5 prestige bonuses (per-class permanent multipliers)
    const pbonus = profile?.prestige_bonuses?.[effectiveClass]?.bonuses || {};
    const prestigeMods = {};
    if (pbonus.dmg)  prestigeMods.playerDamageMult = 1 + (pbonus.dmg * 0.05);
    if (pbonus.hp)   prestigeMods.hpMult = 1 + (pbonus.hp * 0.05);
    if (pbonus.gold) prestigeMods.goldMult = 1 + (pbonus.gold * 0.05);
    // v0.8: weekly runs apply the current weekly mutator globally
    let mutatorMods = {};
    let activeMutator = null;
    if (pendingClassChoice.weekly) {
      activeMutator = mutatorForWeek(currentWeekKey());
      activeMutator.apply(mutatorMods);
    }
    const mods = { ...asc.modifiers, ...curseMods, ...mutatorMods, ...prestigeMods, mastery, useAltStarter };
    const player = newPlayer(seed, classDef, mods);
    // v3.14 GHOST MODE: load previous best for this seed (if any) so the HUD can race the player
    try { setGhostRun(loadGhost(seed)); } catch (e) { setGhostRun(null); }
    // v3.3 TALENT TREE: apply persistent perks owned by the player
    try { applyPerks(player, profile?.perks_owned || []); } catch (e) {}
    // v3.3 PERK: HEIRLOOM — inject one random RARE card at run start
    if (player._perkRareStarter) {
      try {
        const rng = makeRng(seed + '-heirloom');
        const rareId = CARD_POOL_RARE[Math.floor(rng() * CARD_POOL_RARE.length)];
        if (rareId) player.deck = [...player.deck, rareId];
      } catch (e) {}
    }
    if (useDraft && draftDeck && draftDeck.length > 0) {
      player.deck = [...draftDeck];
    }
    const map = generateMap(seed);
    // Pre-fetch ghosts for map overlay
    fetchSeedGhosts(seed).then(g => setSeedGhosts(g));
    setRun({
      seed,
      player,
      map,
      floorIdx: 0,
      startedAt: Date.now(),
      isDaily: !!pendingClassChoice.daily,
      dailyDate: pendingClassChoice.daily || null,
      ascension: ascensionLevel,
      curse: activeCurse,
      weeklyKey: pendingClassChoice.weekly ? currentWeekKey() : null,
      isDraft: useDraft,
      mutator: activeMutator ? activeMutator.id : null,
      useAltStarter,
      // v3.1: pickup floor + trigger telemetry for relics and blessings
      relicPickupFloors: player.relics.reduce((acc, rid) => ({ ...acc, [rid]: 0 }), {}),
      relicTriggers: {},
      blessingPickupFloors: {},
      blessingTriggers: {},
    });
    setPendingClassChoice(null);
    setSelectedClass(effectiveClass);
    setSelectedAscension(ascensionLevel);
    setRunAchievements([]);
    runAchievementsAffixes.current = [];
    // increment total_runs for profile
    const tag = normalizeTag(playerName);
    if (tag) {
      patchProfile(tag, { total_runs_delta: 1 }).then(p => { if (p) setProfile(p); });
    }
    sfx.levelup();
    startMusic();
    // Boons: skip if cursed or daily/party (parity/determinism)
    if (!activeCurse && !pendingClassChoice.daily) {
      const rng = makeRng(seed + '-boons');
      const boons = pickBoons(rng, 3, effectiveClass, mastery);
      setPendingBoon({ boons, seed });
      setScreen('boon');
    } else {
      setScreen('map');
    }
  }, [pendingClassChoice, activeCurse, playerName, draftDeck, profile]);

  const chooseBoon = useCallback((idx) => {
    if (!pendingBoon || !run) return;
    const rng = makeRng(pendingBoon.seed + '-boonact');
    const boon = pendingBoon.boons[idx];
    const runCopy = { ...run, player: { ...run.player, deck: [...run.player.deck], relics: [...run.player.relics] } };
    boon.apply(runCopy, rng);
    // Handle random upgrade side-effect
    if (runCopy.player._upgradeRandom) {
      const times = runCopy.player._upgradeRandom;
      for (let i = 0; i < times; i++) {
        const idxs = runCopy.player.deck.map((cid, j) => canUpgrade(cid) ? j : -1).filter(j => j >= 0);
        if (idxs.length) {
          const pick = idxs[Math.floor(rng() * idxs.length)];
          const upId = upgradedId(runCopy.player.deck[pick]);
          runCopy.player.deck[pick] = upId;
        }
      }
      runCopy.player._upgradeRandom = 0;
    }
    // v0.9: upgrade all RARE cards
    if (runCopy.player._upgradeAllRare) {
      const { CARD_POOL_RARE } = require('../game/cards');
      runCopy.player.deck = runCopy.player.deck.map(cid =>
        CARD_POOL_RARE.includes(cid) && canUpgrade(cid) ? upgradedId(cid) : cid
      );
      runCopy.player._upgradeAllRare = false;
    }
    // v0.9: grant random relics
    if (runCopy.player._grantRandomRelics) {
      const times = runCopy.player._grantRandomRelics;
      for (let i = 0; i < times; i++) {
        const left = ALL_RELIC_IDS.filter(id => !runCopy.player.relics.includes(id));
        if (left.length) runCopy.player.relics.push(left[Math.floor(rng() * left.length)]);
      }
      runCopy.player._grantRandomRelics = 0;
    }
    setRun(runCopy);
    setPendingBoon(null);
    sfx.buff();
    setScreen('map');
  }, [pendingBoon, run]);

  const startQuickRun = useCallback((customSeed) => {
    beginClassSelect(customSeed);
  }, [beginClassSelect]);

  const startWeeklyRun = useCallback((seed) => {
    beginClassSelect(seed, null, null, true);
  }, [beginClassSelect]);

  const enterNode = useCallback((floorIdx, choiceIdx) => {
    if (!run) return;
    const node = run.map[floorIdx][choiceIdx];
    if (!node) return;
    sfx.click();
    // v1.1 COLORPILL: floor-wipe transition on every map choice
    try {
      const el = document.createElement('div');
      el.className = 'floor-wipe';
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 750);
    } catch (e) {}
    const rng = makeRng(run.seed + '-f' + floorIdx + '-c' + choiceIdx);

    if (node.type === NODE_TYPES.MONSTER) {
      const enemy = pickEnemyForFloor(rng, floorIdx + 1);
      // v0.9: 15% chance a random combat modifier appears on a normal fight
      const cmod = rollCombatModifier(rng, 0.15);
      const modBoons = {};
      if (cmod) { cmod.apply(enemy, modBoons); }
      const c = startCombat(
        { ...run.player, __extraDrawBoon: modBoons._extraDraw || 0, _runStreak: runStreak },
        enemy,
        run.seed + '-cmb-' + floorIdx,
        { blockPerTurn: enemy.blockPerTurn }
      );
      c.combatMod = cmod;
      c.rewardGoldMult = modBoons._rewardGoldMult || 1;
      setCombat({ state: c, floorIdx, choiceIdx, kind: 'monster' });
      setScreen('combat');
    } else if (node.type === NODE_TYPES.ELITE) {
      const asc = run.ascension || 0;
      const enemy = pickEliteForFloor(rng, floorIdx + 1, asc);
      // v0.9: elites ALWAYS get a combat modifier
      const cmod = rollCombatModifier(rng, 1.0);
      const modBoons = {};
      if (cmod) { cmod.apply(enemy, modBoons); }
      // v2.2 ELITE SKIP-FOR-CURSE: offer the player a chance to bail for a random curse card.
      // Pre-roll the curse so both branches are deterministic on this floor.
      const cursePool = ['curse_regret','curse_doom','curse_cringe','curse_shame'];
      const skipCurse = cursePool[Math.floor(rng() * cursePool.length)];
      setPendingEliteChoice({
        enemy,
        cmod,
        rewardGoldMult: modBoons._rewardGoldMult || 1,
        floorIdx,
        choiceIdx,
        skipCurse,
      });
      setScreen('elite-choice');
    } else if (node.type === NODE_TYPES.MID_BOSS) {
      const enemy = getMidBoss(rng);
      const c = startCombat({ ...run.player, _runStreak: runStreak }, enemy, run.seed + '-cmb-mb');
      setCombat({ state: c, floorIdx, choiceIdx, kind: 'midboss' });
      setScreen('combat');
    } else if (node.type === NODE_TYPES.BOSS) {
      const enemy = getFinalBoss(rng, run.seed);
      const c = startCombat({ ...run.player, _runStreak: runStreak }, enemy, run.seed + '-cmb-final');
      setCombat({ state: c, floorIdx, choiceIdx, kind: 'boss' });
      setScreen('combat');
    } else if (node.type === NODE_TYPES.SHRINE) {
      const shrine = SHRINES[Math.floor(rng() * SHRINES.length)];
      setPendingShrine({ shrine, floorIdx, choiceIdx, run });
      setScreen('shrine');
    } else if (node.type === NODE_TYPES.TREASURE) {
      // v2.7: rarity-weighted chest selection — no duplicates. Rare types roll less often overall.
      const chosen = new Set();
      const pickedIdxs = [];
      let attempts = 0;
      while (pickedIdxs.length < 3 && attempts < 40) {
        const chest = pickWeightedChest(rng, TREASURE_OUTCOMES);
        const idx = TREASURE_OUTCOMES.findIndex(o => o.key === chest.key);
        if (idx >= 0 && !chosen.has(idx)) {
          chosen.add(idx);
          pickedIdxs.push(idx);
        }
        attempts++;
      }
      setPendingTreasure({ floorIdx, choiceIdx, chestIdxs: pickedIdxs });
      setScreen('treasure');
    } else if (node.type === NODE_TYPES.SHOP) {
      const items = pickN(rng, Object.keys(CARDS).filter(k => !k.endsWith('+') && !['yap','cope'].includes(k)), 4);
      const relicsAvail = pickN(rng, ALL_RELIC_IDS.filter(r => !run.player.relics.includes(r)), 1);
      const shopMult = run.player.mods?.shopMult || 1;
      setPendingShop({
        cards: items.map(id => ({ id, price: Math.floor((30 + rng()*40) * shopMult) })),
        relics: relicsAvail.map(id => ({ id, price: Math.floor((120 + rng()*60) * shopMult) })),
        removePrice: Math.floor(75 * shopMult),
        removeUsed: false,
        // v2.3 QoL: shop rerolls — starts cheap, escalates each use
        rerollBasePrice: Math.floor(40 * shopMult),
        rerollsUsed: 0,
        // v3.10 SHOP DEPTH: new services
        upgradePrice: Math.floor(85 * shopMult),
        upgradeUsed: false,
        blessingPrice: Math.floor(220 * shopMult),
        blessingUsed: false,
        floorIdx, choiceIdx,
        _seed: run.seed + '-shop-' + floorIdx, // stable base for rerolls
        _shopMult: shopMult,
      });
      setScreen('shop');
    } else if (node.type === NODE_TYPES.REST) {
      setPendingRest({ floorIdx, choiceIdx });
      setScreen('rest');
    } else if (node.type === NODE_TYPES.EVENT) {
      const events = [
        {
          title: 'THE BURNER PHONE',
          text: 'A shady dude offers you a burner. He wants 10 HP.',
          options: [
            { label: 'TAKE IT (-10 HP, gain relic)', act: (r) => {
              const relicsLeft = ALL_RELIC_IDS.filter(id => !r.player.relics.includes(id));
              if (relicsLeft.length) r.player.relics.push(relicsLeft[Math.floor(rng()*relicsLeft.length)]);
              r.player.hp = Math.max(1, r.player.hp - 10);
            }},
            { label: 'NAH BRO', act: () => {} },
          ],
        },
        {
          title: 'BLESSING OF THE RIZZLER',
          text: 'A cursed shrine offers power.',
          options: [
            { label: 'PRAY (+15 max HP)', act: (r) => { r.player.maxHp += 15; r.player.hp += 15; } },
            { label: 'DESECRATE (+80 gold, -5 max HP)', act: (r) => { r.player.gold += 80; r.player.maxHp = Math.max(20, r.player.maxHp - 5); r.player.hp = Math.min(r.player.hp, r.player.maxHp); } },
            { label: 'IGNORE', act: () => {} },
          ],
        },
        {
          title: 'DEAD TWITTER CHANNEL',
          text: 'You find a decaying feed. It knows things.',
          options: [
            { label: 'DOOMSCROLL (gain a random RARE card)', act: (r) => {
              const rare = ['brainrotpower','sigmapower','brainrotbeam','sigmastare','aura','megamind','slopdunk','amplifier'];
              r.player.deck.push(rare[Math.floor(rng()*rare.length)]);
            }},
            { label: 'CLOSE APP', act: () => {} },
          ],
        },
        {
          title: 'CURSED ENERGY DRINK',
          text: 'A can of Slop Zero fizzes ominously.',
          options: [
            { label: 'CHUG (+30 gold + heal 5)', act: (r) => { r.player.gold += 30; r.player.hp = Math.min(r.player.maxHp, r.player.hp + 5); } },
            { label: 'CRUSH IT (+50 gold)', act: (r) => { r.player.gold += 50; } },
          ],
        },
        {
          title: 'THE PAWN SHOP',
          text: 'A gremlin behind the counter eyes your gear.',
          options: [
            { label: 'SELL A RANDOM CARD (+40 gold)', act: (r) => {
              if (r.player.deck.length > 4) {
                const idx = Math.floor(rng() * r.player.deck.length);
                r.player.deck.splice(idx, 1);
                r.player.gold += 40;
              }
            }},
            { label: 'BUY A MYSTERY CARD (-40 gold)', act: (r) => {
              if (r.player.gold >= 40) {
                r.player.gold -= 40;
                const pool = ['gooncannon','seethe','malding','meditate','hardstance','posture'];
                r.player.deck.push(pool[Math.floor(rng()*pool.length)]);
              }
            }},
            { label: 'WALK OUT', act: () => {} },
          ],
        },
        {
          title: 'MAINSTREAMED',
          text: 'You go viral. Blessing? Curse?',
          options: [
            { label: 'RIDE THE WAVE (+50 gold, +2 max HP)', act: (r) => { r.player.gold += 50; r.player.maxHp += 2; r.player.hp += 2; } },
            { label: 'FIGHT THE FEED (heal to full)', act: (r) => { r.player.hp = r.player.maxHp; } },
          ],
        },
        // v1.8 DEPTH: Slop Trader — trade a random card for a random RARE
        {
          title: 'SLOP TRADER',
          text: 'A hooded gremlin offers to trade a card from your deck for something rarer.',
          options: [
            { label: 'ACCEPT (lose 1 random card, gain 1 RARE)', act: (r) => {
              if (r.player.deck.length > 5) {
                const idx = Math.floor(rng() * r.player.deck.length);
                r.player.deck.splice(idx, 1);
              }
              const rarePool = CARD_POOL_RARE.filter(id => !/\+$/.test(id));
              r.player.deck.push(rarePool[Math.floor(rng() * rarePool.length)]);
            }},
            { label: 'DECLINE (heal 5 HP)', act: (r) => { r.player.hp = Math.min(r.player.maxHp, r.player.hp + 5); } },
          ],
        },
        // v1.8 DEPTH: Cursed Altar
        {
          title: 'CURSED ALTAR',
          text: 'Ancient runes glow. The altar demands a sacrifice.',
          options: [
            { label: 'ACCEPT (add REGRET curse, +80 gold)', act: (r) => {
              r.player.deck.push('curse_regret'); r.player.gold += 80;
            }},
            { label: 'ACCEPT (add DOOM WHISPER curse, +2 max HP forever)', act: (r) => {
              r.player.deck.push('curse_doom'); r.player.maxHp += 2; r.player.hp += 2;
            }},
            { label: 'FLEE (nothing happens)', act: () => {} },
          ],
        },
        // v2.0 AVATARS: 5 new events for depth
        {
          title: 'SHRINE OF DUPES',
          text: 'A mirror shrine offers to clone one of your cards.',
          options: [
            { label: 'DUPLICATE A RANDOM CARD', act: (r) => {
              if (r.player.deck.length > 0) {
                const idx = Math.floor(rng() * r.player.deck.length);
                r.player.deck.push(r.player.deck[idx]);
              }
            }},
            { label: 'REFUSE (heal 8 HP)', act: (r) => { r.player.hp = Math.min(r.player.maxHp, r.player.hp + 8); } },
          ],
        },
        {
          title: 'SPONSORED SEGMENT',
          text: 'A sponsor slaps their logo on your face. Cash flows, blood flows.',
          options: [
            { label: 'ACCEPT (-15 HP, +100 gold, +1 random RELIC)', act: (r) => {
              r.player.hp = Math.max(1, r.player.hp - 15);
              r.player.gold += 100;
              const left = ALL_RELIC_IDS.filter(id => !r.player.relics.includes(id));
              if (left.length) r.player.relics.push(left[Math.floor(rng() * left.length)]);
            }},
            { label: 'CANCEL SPONSOR (nothing)', act: () => {} },
          ],
        },
        {
          title: 'HYPE TRAIN',
          text: 'Chat is FEEDING you power. Do you drink from the pipe?',
          options: [
            { label: 'DRINK (+1 MAX ENERGY forever!)', act: (r) => {
              r.player.maxEnergy = (r.player.maxEnergy || 3) + 1;
            }},
            { label: 'DECLINE (+80 gold)', act: (r) => { r.player.gold += 80; } },
          ],
        },
        {
          title: 'FRAUD ALERT',
          text: 'A cursed follower wants to remove a card from your deck… for free.',
          options: [
            { label: 'REMOVE A RANDOM CARD', act: (r) => {
              if (r.player.deck.length > 5) {
                const idx = Math.floor(rng() * r.player.deck.length);
                r.player.deck.splice(idx, 1);
              }
            }},
            { label: 'REPORT & BLOCK (nothing)', act: () => {} },
          ],
        },
        {
          title: 'MEME COUNCIL',
          text: 'Three council members bicker. Pick their verdict.',
          options: [
            { label: 'RIZZ COUNCILOR: +25 gold, +5 HP', act: (r) => { r.player.gold += 25; r.player.hp = Math.min(r.player.maxHp, r.player.hp + 5); } },
            { label: 'MOG COUNCILOR: random UNCOMMON card', act: (r) => {
              const uncommons = CARD_POOL_UNCOMMON.filter(id => !/\+$/.test(id));
              r.player.deck.push(uncommons[Math.floor(rng() * uncommons.length)]);
            }},
            { label: 'DOOM COUNCILOR: +3 max HP forever', act: (r) => { r.player.maxHp += 3; r.player.hp += 3; } },
          ],
        },
        // v3.4 CYOA events — SHRINE, TRICKSTER, MERC, and 3 more
        {
          title: 'BLOOD SHRINE',
          text: 'An old shrine drinks HP for cursed knowledge.',
          options: [
            { label: 'SACRIFICE 15 HP → gain +2 STRENGTH permanently', act: (r) => {
              r.player.hp = Math.max(1, r.player.hp - 15);
              r.player._permaStrength = (r.player._permaStrength || 0) + 2;
            }},
            { label: 'SACRIFICE 5 HP → +50 gold', act: (r) => {
              r.player.hp = Math.max(1, r.player.hp - 5);
              r.player.gold += 50;
            }},
            { label: 'IGNORE (nothing happens)', act: () => {} },
          ],
        },
        {
          title: 'THE TRICKSTER',
          text: 'A hooded goblin shows you two closed hands. Pick one.',
          options: [
            { label: 'LEFT HAND (50/50: +80 gold OR -30 gold)', act: (r) => {
              if (rng() < 0.5) r.player.gold += 80;
              else r.player.gold = Math.max(0, r.player.gold - 30);
            }},
            { label: 'RIGHT HAND (50/50: +1 max energy OR -8 HP)', act: (r) => {
              if (rng() < 0.5) r.player.maxEnergy = (r.player.maxEnergy || 3) + 1;
              else r.player.hp = Math.max(1, r.player.hp - 8);
            }},
            { label: 'WALK AWAY (+1 slop token)', act: (r) => { r.player._eventTokenBonus = (r.player._eventTokenBonus || 0) + 1; } },
          ],
        },
        {
          title: 'HIRE A MERC',
          text: 'A brutalist merc offers to smash your next elite for a fee.',
          options: [
            { label: 'PAY 60 GOLD → auto-clear next elite', act: (r) => {
              if (r.player.gold >= 60) {
                r.player.gold -= 60;
                r.player._mercAvail = true;
              }
            }},
            { label: 'HAGGLE (roll d6): if ≤3 free merc, else -20 HP', act: (r) => {
              if (rng() < 0.5) r.player._mercAvail = true;
              else r.player.hp = Math.max(1, r.player.hp - 20);
            }},
            { label: 'DECLINE', act: () => {} },
          ],
        },
        {
          title: 'ABANDONED STREAM',
          text: 'A dead streamer\'s setup gathers dust. Something valuable inside?',
          options: [
            { label: 'LOOT (+40 gold, +1 random UNCOMMON card)', act: (r) => {
              r.player.gold += 40;
              const uncommons = CARD_POOL_UNCOMMON.filter(id => !/\+$/.test(id));
              r.player.deck.push(uncommons[Math.floor(rng() * uncommons.length)]);
            }},
            { label: 'RESPECT THE DEAD (+10 max HP forever)', act: (r) => {
              r.player.maxHp += 10; r.player.hp += 10;
            }},
          ],
        },
        {
          title: 'DIGITAL RITUAL',
          text: 'Chat is chanting an occult symbol at you. It vibes.',
          options: [
            { label: 'JOIN THE RITUAL (upgrade a random card)', act: (r) => {
              // Try up to 5 times to find an upgradable card
              for (let tries = 0; tries < 5; tries++) {
                const idx = Math.floor(rng() * r.player.deck.length);
                const cardId = r.player.deck[idx];
                if (cardId && !/\+$/.test(cardId)) {
                  r.player.deck[idx] = cardId + '+';
                  break;
                }
              }
            }},
            { label: 'RESIST (nothing happens)', act: () => {} },
          ],
        },
      ];
      setPendingEvent({ event: events[Math.floor(rng()*events.length)], floorIdx, choiceIdx });
      setScreen('event');
    }
  }, [run]);

  // v1.8 DEPTH: UNDO — snapshot state before play so we can revert once per turn
  const undoRef = useRef(null);
  const undoLastCard = useCallback(() => {
    if (!undoRef.current || !combat) return false;
    setCombat({ ...combat, state: undoRef.current });
    undoRef.current = null; // one-shot
    return true;
  }, [combat]);
  const canUndo = !!undoRef.current;

  const doPlayCard = useCallback((handIdx) => {
    if (!combat) return;
    // Snapshot for undo (deep-ish clone of piles + player)
    undoRef.current = {
      ...combat.state,
      hand: [...combat.state.hand],
      drawPile: [...combat.state.drawPile],
      discardPile: [...combat.state.discardPile],
      exhaustPile: [...combat.state.exhaustPile],
      player: { ...combat.state.player },
      enemy: { ...combat.state.enemy },
      log: [...(combat.state.log || [])],
    };
    const s = { ...combat.state, hand: [...combat.state.hand], drawPile: [...combat.state.drawPile], discardPile: [...combat.state.discardPile], exhaustPile: [...combat.state.exhaustPile], player: { ...combat.state.player }, enemy: { ...combat.state.enemy } };
    const cardId = s.hand[handIdx];
    const card = CARDS[cardId];
    if (!card || s.player.energy < card.cost) return;
    if (card.requiresBlock && s.player.block <= 0) { setFloatingText('NEED BLOCK'); return; }
    // v2.8 QoL: Card Discovery — mark this card as PLAYED (skip status/curse unplayables)
    if (!card.unplayable) { try { bumpCardStat(cardId, 'played'); } catch (e) {} }
    const beforeE = s.enemy.hp;
    playCard(s, handIdx);
    if (card.type === 'attack') sfx.attack();
    else if (card.type === 'skill') { if (card.block) sfx.block(); else if (card.heal) sfx.heal(); else if (card.energy) sfx.energy(); else sfx.cardPlay(); }
    else if (card.type === 'power') sfx.buff();
    if (s.enemy.hp < beforeE) sfx.hit();

    // Achievement tracking
    if (s.biggestSingleHit >= 40) pushAchievement('glass_cannon');
    if (s.powersPlayedThisCombat >= 3) pushAchievement('power_trip');
    // v0.9.5 achievement wiring
    if (s._synergyCount >= 5) pushAchievement('synergy_master');
    if (s._achievement_xCostGod) pushAchievement('x_cost_god');
    if (s.evolveCounts && Object.keys(s.evolveCounts).some(k => k === 'apex_slop' || (k === 'blooming_slop' && s.evolveCounts[k] > 0))) pushAchievement('evolved');
    if (run?.player?.deck) {
      const cursedInDeck = run.player.deck.filter(cid => (CARDS[cid] || {}).cursed).length;
      if (cursedInDeck >= 3) pushAchievement('cursed_deck');
      // set_complete: check if any relic set is active
      const anySet = activeRelicSets(run.player.relics || []).length > 0;
      if (anySet) pushAchievement('set_complete');
    }
    // affix_slayer: track affix defeats per run
    if (s.enemy?.affix && s.victory) {
      const seen = new Set(runAchievementsAffixes.current || []);
      seen.add(s.enemy.affix);
      runAchievementsAffixes.current = Array.from(seen);
      if (seen.size >= 3) pushAchievement('affix_slayer');
    }

    setCombat(c => ({ ...c, state: s }));
    if (s.ended) handleCombatEnd(s);
  }, [combat, pushAchievement]);

  const doEndTurn = useCallback(() => {
    if (!combat) return;
    undoRef.current = null; // v1.8: turn ends → discard undo snapshot
    const s = { ...combat.state, hand: [...combat.state.hand], drawPile: [...combat.state.drawPile], discardPile: [...combat.state.discardPile], exhaustPile: [...combat.state.exhaustPile], player: { ...combat.state.player }, enemy: { ...combat.state.enemy } };
    const beforeP = s.player.hp;
    endTurn(s);
    if (s.player.hp < beforeP) sfx.hit();
    else sfx.cardDraw();
    setCombat(c => ({ ...c, state: s }));
    if (s.ended) handleCombatEnd(s);
  }, [combat]);

  const handleCombatEnd = useCallback((s) => {
    if (!combat) return;
    // v3.9 CINEMA: defer post-combat screen transition when a boss/elite is slain,
    // so Combat.jsx stays mounted for the final-blow cinematic (~2.1s).
    const _isSpecialKill = !!(s.victory && (s.enemy?.isBoss || s.enemy?.isElite));
    const goToScreen = (name) => {
      if (_isSpecialKill) setTimeout(() => setScreen(name), 2100);
      else setScreen(name);
    };
    // v1.0: record combat outcome to run history
    recordFloorEvent({
      kind: 'combat',
      floor: (combat.floorIdx || 0) + 1,
      enemyName: s.enemy?.name,
      enemyId: s.enemy?.id,
      isElite: !!s.enemy?.isElite,
      isBoss: !!s.enemy?.isBoss,
      victory: !!s.victory,
      hpStart: run?.player?.hp,
      hpEnd: s.player.hp,
      dmgTaken: s.dmgTakenThisCombat || 0,
      biggestHit: s.biggestSingleHit || 0,
      turns: s.turn || 1,
      synergies: s._synergyCount || 0,
      affix: s.enemy?.affix,
    });
    const updatedPlayer = {
      ...run.player,
      hp: s.player.hp,
      // v3.16 FIX: DO NOT propagate s.player.strength back to run.player.strength.
      // The combat state's strength includes permaStr + relic contributions applied at combat start.
      // Overwriting run.player.strength double-counted permaStr on every subsequent combat, causing
      // chest boosts and PERMA-MOG kills to inflate uncontrollably. Base strength changes from boons
      // (Sigma Mindset, Sigma signature) mutate run.player.strength directly at pick time, and
      // combat-earned permanent gains (MOG kills, PERMA-MOG powers) now update _permaStrength instead.
      strength: run.player.strength || 0,
      // v0.9: propagate perma buffs from combat state back to run
      _permaStrength: s.player._permaStrength || run.player._permaStrength || 0,
      _permaMaxHp: s.player._permaMaxHp || run.player._permaMaxHp || 0,
      _permaDamageMult: s.player._permaDamageMult || run.player._permaDamageMult || 1,
      _permaHpApplied: s.player._permaHpApplied || run.player._permaHpApplied,
      _divineTouchGrassUsed: s.player._divineTouchGrassUsed || run.player._divineTouchGrassUsed,
      gold: (s.player.gold !== undefined ? s.player.gold : run.player.gold),
      maxHp: s.player.maxHp || run.player.maxHp,
    };
    // v2.3 QoL: aggregate run-wide stats for the RunStatsHUD floater
    const priorStats = run._runStats || { floorsCleared: 0, cardsPlayed: 0, dmgDealt: 0, dmgTaken: 0, biggestHit: 0, turnsUsed: 0, kills: 0, synergiesFired: 0 };
    const _runStats = {
      floorsCleared: priorStats.floorsCleared + (s.victory ? 1 : 0),
      cardsPlayed: priorStats.cardsPlayed + (s.cardsPlayedThisCombat || 0),
      dmgDealt: priorStats.dmgDealt + (s.dmgDealtThisCombat || 0),
      dmgTaken: priorStats.dmgTaken + (s.dmgTakenThisCombat || 0),
      biggestHit: Math.max(priorStats.biggestHit, s.biggestSingleHit || 0),
      turnsUsed: priorStats.turnsUsed + (s.turn || 0),
      kills: priorStats.kills + (s.victory ? 1 : 0),
      synergiesFired: priorStats.synergiesFired + (s._synergyCount || 0),
    };
    setRun(r => r ? { ...r, _runStats } : r);
    if (s.victory) {
      sfx.victory();
      // v1.1 COLORPILL: color burst on victory
      triggerBurst(s.enemy?.isBoss ? 'rainbow' : 'gold');
      // v1.9 MOG LICENSE relic: fast kill (turn <= 2) bumps max HP once per combat
      if ((s.turn || 1) <= 2 && updatedPlayer.relics?.includes('moglicense') && !s._mogLicenseFired) {
        updatedPlayer.maxHp += 6;
        updatedPlayer.hp = Math.min(updatedPlayer.maxHp, updatedPlayer.hp + 6);
        s._mogLicenseFired = true;
      }
      // Codex: mark this enemy as defeated
      const tag = normalizeTag(playerName);
      if (tag && s.enemy?.id) {
        patchProfile(tag, { codex_entry: s.enemy.id }).then(p => {
          if (p) {
            setProfile(p);
            // v1.5: Codex progress achievements
            const entries = p.codex_entries || [];
            if (entries.length >= 10) pushAchievement('codex_10');
            if (entries.length >= 25) pushAchievement('codex_25');
            const totalEnemies = Object.keys(ENEMIES || {}).length;
            if (entries.length >= totalEnemies) pushAchievement('codex_all');
            if (s.enemy?.id === 'boss_cursed_emoji') pushAchievement('cursed_boss');
          }
        });
      }
      // Achievements
      pushAchievement('first_blood');
      if (s.dmgTakenThisCombat === 0) pushAchievement('perfect');
      if (updatedPlayer.hp === 1) pushAchievement('clutch');
      if (updatedPlayer.hp <= 5) pushAchievement('broke_but_woke');
      if (updatedPlayer.gold >= 300) pushAchievement('hoarder');
      if (updatedPlayer.relics.length >= 4) pushAchievement('relic_hunter');
      if (updatedPlayer.deck.length >= 30) pushAchievement('fat_deck');

      const rng = makeRng(run.seed + '-rw-' + combat.floorIdx);
      const goldMult = updatedPlayer.mods?.goldMult ?? 1;
      const goldBase = combat.kind === 'boss' ? 200 : combat.kind === 'midboss' ? 120 : (combat.kind === 'elite' ? 55 : 25);
      // v3.4 LIVE-STREAMED affix: 2x gold + tokens on kill
      const _livestream = s.enemy?.livestreamed ? 2 : 1;
      const gold = Math.floor(goldBase * (updatedPlayer.relics.includes('cursedcoin') ? 1.5 : 1) * goldMult * _livestream);
      updatedPlayer.gold += gold;
      // v2.1 BOSS BLESSING: HEALING FACTOR — heal N after every combat victory
      const _postHeal = updatedPlayer._blessMods?.blessPostCombatHeal || 0;
      if (_postHeal > 0) {
        updatedPlayer.hp = Math.min(updatedPlayer.maxHp, updatedPlayer.hp + _postHeal);
      }
      // v2.1 BOSS BLESSING: EXTRA LOOT — +1 to reward count
      const _blessExtra = updatedPlayer._blessMods?.blessExtraRewardCards || 0;
      const rewardCount = (updatedPlayer.mods?.rewardCount ?? (updatedPlayer.relics.includes('streamdeck') ? 4 : 3)) + _blessExtra;
      // v2.8 DIVINE: allow up to one divine per run, floor 5+. Filter used-once ones (touchgrass).
      // v3.0: include class-specific divines in the pool
      const _divineDropped = !!run.divineDropped;
      const _classDivines = CARD_POOL_DIVINE_CLASS[updatedPlayer.classId] || [];
      const divinePool = [...CARD_POOL_DIVINE, ..._classDivines].filter(id => {
        if (id === 'divine_touchgrass' && updatedPlayer._divineTouchGrassUsed) return false;
        return true;
      });
      const rewardCards = generateCardReward(
        rng, combat.floorIdx + 1, Math.max(1, rewardCount), updatedPlayer.classId,
        { divineAllowed: !_divineDropped, divinePool, divineBoost: !!updatedPlayer._perkDivineBoost }
      );
      // Mark divine as dropped if any was included
      const _rewardHasDivine = rewardCards.some(id => CARD_POOL_DIVINE.includes(id));
      if (_rewardHasDivine) setRun(r => r ? { ...r, divineDropped: true } : r);
      setRun(r => ({ ...r, player: updatedPlayer }));

      if (combat.kind === 'midboss') pushAchievement('slop_slayer');
      if (combat.kind === 'boss') {
        pushAchievement('final_arc');
        if (updatedPlayer.classId === 'rizzler') pushAchievement('rizzler_win');
        if (updatedPlayer.classId === 'sigma') pushAchievement('sigma_win');
        if (updatedPlayer.classId === 'gooner') pushAchievement('gooner_win');
        if (updatedPlayer.deck.length <= 12) pushAchievement('lean_deck');
        if (run.isDaily) pushAchievement('daily_grinder');
        if (activeCurse) pushAchievement('cursed_win');
        // v3.19 CURSED CENTURY: victory with 100 stacked curses.
        if ((activeCurseStack || []).length >= 100) pushAchievement('cursed_century');
        const asc = run.ascension || 0;
        if (asc >= 0) pushAchievement('ascension_1'); // beat any run unlocks A1
        if (asc >= 2) pushAchievement('ascension_3');
        if (asc >= 5) pushAchievement('ascension_5');
        stopMusic();
        // v1.1: bump run streak on final boss victory + celebratory streak burst
        bumpStreak(true);
        setTimeout(() => triggerBurst('streak'), 700);
        // v1.2: legendary boom on streak continuation
        try { sfx.streakBoom(); } catch (e) {}
        // v1.5: streak achievements + status_survivor
        const _nextStreak = runStreak + 1;
        if (_nextStreak >= 5) pushAchievement('streak_5');
        if (_nextStreak >= 10) pushAchievement('streak_10');
        // Count CURSE cards (unplayable, curse:true) in final deck
        const curseCount = (updatedPlayer.deck || []).filter(cid => {
          const cc = CARDS[cid];
          return cc && cc.curse === true;
        }).length;
        if (curseCount >= 3) pushAchievement('status_survivor');
        const _finalScore = computeScore(updatedPlayer, combat.floorIdx + 1, true, run.ascension || 0);
        // v1.1: streak-scaled bonus tokens (bonus % of score, capped)
        const _streakBonus = Math.min(1500, Math.floor(_finalScore * Math.min(0.5, (runStreak + 1) * 0.05)));
        setLastRunSummary({
          victory: true, bossDefeated: true,
          floor: combat.floorIdx + 1,
          score: _finalScore,
          streak: runStreak + 1,
          streakBonus: _streakBonus,
          seed: run.seed, player: updatedPlayer,
          isDaily: !!run.isDaily, dailyDate: run.dailyDate,
          ascension: run.ascension || 0,
          curse: activeCurse,
          curseStack: activeCurseStack, // v3.10 STACKED CURSES
          // v3.1: forward pickup floors for tooltip
          relicPickupFloors: { ...(run.relicPickupFloors || {}) },
          relicTriggers: { ...(run.relicTriggers || {}) },
          blessingPickupFloors: { ...(run.blessingPickupFloors || {}) },
          blessingTriggers: { ...(run.blessingTriggers || {}) },
          history: [...(run.history || []), {
            kind: 'combat',
            floor: (combat.floorIdx || 0) + 1,
            enemyName: s.enemy?.name, enemyId: s.enemy?.id,
            isBoss: true, victory: true,
            hpStart: run?.player?.hp, hpEnd: s.player.hp,
            dmgTaken: s.dmgTakenThisCombat || 0, biggestHit: s.biggestSingleHit || 0,
            turns: s.turn || 1, ts: Date.now(),
          }],
        });
        goToScreen('gameover');
      } else {
        // v2.4 POST-COMBAT PILE SUMMARY: show recap screen first, then proceed to blessing/reward.
        const recap = {
          kind: combat.kind,
          floor: combat.floorIdx + 1,
          enemyName: combat.state?.enemy?.name || 'ENEMY',
          enemySprite: combat.state?.enemy?.sprite || '💀',
          turns: s.turn || 1,
          cardsPlayed: s.cardsPlayedThisCombat || 0,
          dmgDealt: s.dmgDealtThisCombat || 0,
          dmgTaken: s.dmgTakenThisCombat || 0,
          biggestHit: s.biggestSingleHit || 0,
          exhausted: (s.exhaustPile || []).length,
          finalHp: s.player.hp,
          maxHp: s.player.maxHp,
          gold,
          rewardPreviewCount: rewardCards.length,
        };
        // Cache the next screen data on the recap so CONTINUE can advance to it.
        if (combat.kind === 'midboss') {
          const bRng = makeRng(run.seed + '-bless-' + combat.floorIdx);
          const owned = updatedPlayer._blessings || [];
          const offers = rollBlessingOffers(bRng, owned);
          if (offers.length > 0) {
            recap.nextScreen = 'blessing';
            recap.nextBlessing = {
              offers,
              floorIdx: combat.floorIdx,
              choiceIdx: combat.choiceIdx,
              pendingReward: { cards: rewardCards, gold, floorIdx: combat.floorIdx, choiceIdx: combat.choiceIdx },
            };
          }
        }
        if (!recap.nextScreen) {
          recap.nextScreen = 'reward';
          recap.nextReward = { cards: rewardCards, gold, floorIdx: combat.floorIdx, choiceIdx: combat.choiceIdx };
        }
        setPendingRecap(recap);
        goToScreen('recap');
      }
    } else {
      sfx.defeat();
      sfx.slam();
      stopMusic();
      // v1.1 COLORPILL: death shatter burst + streak reset
      const _lostStreak = runStreak;
      triggerBurst('shatter');
      bumpStreak(false);
      setLastRunSummary({
        victory: false, bossDefeated: false,
        floor: combat.floorIdx + 1,
        score: computeScore(updatedPlayer, combat.floorIdx + 1, false, run.ascension || 0),
        streakLost: _lostStreak,
        seed: run.seed, player: updatedPlayer,
        isDaily: !!run.isDaily, dailyDate: run.dailyDate,
        ascension: run.ascension || 0,
        curse: activeCurse,
        curseStack: activeCurseStack, // v3.10 STACKED CURSES
        // v3.1: forward pickup floors for tooltip
        relicPickupFloors: { ...(run.relicPickupFloors || {}) },
        relicTriggers: { ...(run.relicTriggers || {}) },
        blessingPickupFloors: { ...(run.blessingPickupFloors || {}) },
        blessingTriggers: { ...(run.blessingTriggers || {}) },
        history: [...(run.history || []), {
          kind: 'combat', floor: (combat.floorIdx || 0) + 1,
          enemyName: s.enemy?.name, enemyId: s.enemy?.id,
          victory: false, hpStart: run?.player?.hp, hpEnd: 0,
          dmgTaken: s.dmgTakenThisCombat || 0, ts: Date.now(),
        }],
      });
      setScreen('gameover');
    }
  }, [combat, run, pushAchievement, activeCurse, triggerBurst, runStreak, bumpStreak, recordFloorEvent, playerName]);

  const takeRewardCard = useCallback((cardId) => {
    if (!pendingReward) return;
    sfx.cardPlay();
    // v2.8 QoL: Card Discovery — mark this reward card as SEEN and any siblings as SEEN
    try {
      (pendingReward.cards || []).forEach(cid => bumpCardStat(cid, 'seen'));
    } catch (e) {}
    // v3.0 DIVINE DISCOVERY: increment profile counter when a divine card is taken as reward
    try {
      if (isDivineCard(cardId)) {
        const tag = normalizeTag(profile?.tag || run?.player?.tag);
        if (tag) patchProfile(tag, { divine_picked: cardId.replace(/\+$/, '') }).then(p => { if (p) setProfile(p); });
      }
    } catch (e) {}
    recordFloorEvent({ kind: 'reward', floor: (pendingReward.floorIdx || 0) + 1, cardId, gold: pendingReward.gold });
    // v1.9 GIGA COIN relic: +8 gold on card reward pick
    let bonusGold = 0;
    if (run?.player?.relics?.includes('gigacoin')) bonusGold = 8;
    // v1.1: burst on rare card pick + v1.2 rarity fanfare
    if (CARD_POOL_RARE.includes(cardId) || CARD_POOL_RARE.includes(cardId.replace(/\+$/, ''))) {
      triggerBurst('rainbow');
      try {
        if (/\+$/.test(cardId)) sfx.legendaryChime();
        else sfx.rareChime();
      } catch (e) {}
    }
    // v1.5: local "cards seen" tracker + achievements
    try {
      const seen = new Set(JSON.parse(localStorage.getItem('friendslop.cards_seen') || '[]'));
      seen.add(cardId.replace(/\+$/, ''));
      localStorage.setItem('friendslop.cards_seen', JSON.stringify([...seen]));
      if (seen.size >= 50) pushAchievement('cards_seen_50');
      // Every base rare seen?
      const allRare = CARD_POOL_RARE.filter(id => !/\+$/.test(id));
      if (allRare.every(id => seen.has(id))) pushAchievement('cards_seen_all_rare');
    } catch (e) {}
    setRun(r => ({ ...r, player: { ...r.player, deck: [...r.player.deck, cardId], gold: r.player.gold + bonusGold }, floorIdx: pendingReward.floorIdx + 1 }));
    setPendingReward(null);
    setScreen('map');
  }, [pendingReward, recordFloorEvent, triggerBurst, pushAchievement, run]);

  const skipReward = useCallback(() => {
    if (!pendingReward) return;
    setRun(r => ({ ...r, floorIdx: pendingReward.floorIdx + 1 }));
    setPendingReward(null);
    setScreen('map');
  }, [pendingReward]);

  // v2.4 POST-COMBAT PILE SUMMARY — continue from recap to next screen (blessing or reward)
  const dismissRecap = useCallback(() => {
    if (!pendingRecap) return;
    const r = pendingRecap;
    setPendingRecap(null);
    if (r.nextScreen === 'blessing' && r.nextBlessing) {
      setPendingBlessing(r.nextBlessing);
      setScreen('blessing');
    } else if (r.nextScreen === 'reward' && r.nextReward) {
      setPendingReward(r.nextReward);
      setScreen('reward');
    } else {
      setScreen('map');
    }
  }, [pendingRecap]);

  // v2.2 ELITE SKIP-FOR-CURSE
  const eliteFight = useCallback(() => {
    if (!pendingEliteChoice) return;
    const { enemy, cmod, rewardGoldMult, floorIdx, choiceIdx } = pendingEliteChoice;
    const runStreak = run?.streakThisRun || 0;
    const c = startCombat({ ...run.player, _runStreak: runStreak }, enemy, run.seed + '-cmb-' + floorIdx, { blockPerTurn: enemy.blockPerTurn });
    c.combatMod = cmod;
    c.rewardGoldMult = rewardGoldMult;
    setCombat({ state: c, floorIdx, choiceIdx, kind: 'elite' });
    setPendingEliteChoice(null);
    setScreen('combat');
  }, [pendingEliteChoice, run]);

  const eliteSkip = useCallback(() => {
    if (!pendingEliteChoice) return;
    const { floorIdx, choiceIdx, skipCurse } = pendingEliteChoice;
    try { sfx.curseChime && sfx.curseChime(); } catch (e) {}
    setRun(r => {
      if (!r) return r;
      const newDeck = [...r.player.deck, skipCurse];
      return { ...r, floorIdx: floorIdx + 1, player: { ...r.player, deck: newDeck } };
    });
    recordFloorEvent({ kind: 'elite_skip', floor: floorIdx + 1, curseId: skipCurse, choiceIdx });
    setPendingEliteChoice(null);
    setScreen('map');
  }, [pendingEliteChoice, recordFloorEvent]);

  // v2.1 BOSS BLESSING: pick one, apply modifiers, then proceed to normal card reward.
  const takeBlessing = useCallback((blessingId) => {
    if (!pendingBlessing) return;
    const bDef = BLESSINGS[blessingId];
    if (!bDef) return;
    try { sfx.legendaryChime && sfx.legendaryChime(); } catch (e) {}
    try { triggerBurst && triggerBurst('rainbow'); } catch (e) {}
    setRun(r => {
      if (!r) return r;
      const player = { ...r.player };
      const bMods = { ...(player._blessMods || {}) };
      bDef.apply(bMods, player);
      player._blessMods = bMods;
      player._blessings = [ ...(player._blessings || []), bDef.id ];
      // v3.1: track pickup floor for the tooltip
      const blessingPickupFloors = { ...(r.blessingPickupFloors || {}), [bDef.id]: (pendingBlessing.floorIdx || 0) + 1 };
      return { ...r, player, blessingPickupFloors };
    });
    recordFloorEvent({ kind: 'blessing', floor: (pendingBlessing.floorIdx || 0) + 1, blessingId: bDef.id, blessingName: bDef.name });
    // v2.5 BLESSING ARCHIVE: persist pick count to profile
    (async () => {
      const tag = normalizeTag(playerName);
      if (tag) { try { await patchProfile(tag, { blessing_picked: bDef.id }); } catch (e) {} }
    })();
    setPendingReward(pendingBlessing.pendingReward);
    setPendingBlessing(null);
    setScreen('reward');
  }, [pendingBlessing, recordFloorEvent, triggerBurst]);

  const buyCard = useCallback((idx) => {
    if (!pendingShop) return;
    const item = pendingShop.cards[idx];
    if (!item) return;
    if (run.player.gold < item.price) { setFloatingText('NOT ENOUGH GOLD'); return; }
    sfx.gold();
    setRun(r => ({ ...r, player: { ...r.player, gold: r.player.gold - item.price, deck: [...r.player.deck, item.id] } }));
    setPendingShop(s => ({ ...s, cards: s.cards.map((c,i) => i===idx ? null : c) }));
  }, [pendingShop, run]);

  const buyRelic = useCallback((idx) => {
    if (!pendingShop) return;
    const item = pendingShop.relics[idx];
    if (!item) return;
    if (run.player.gold < item.price) { setFloatingText('NOT ENOUGH GOLD'); return; }
    sfx.gold();
    setRun(r => ({
      ...r,
      player: { ...r.player, gold: r.player.gold - item.price, relics: [...r.player.relics, item.id] },
      relicPickupFloors: { ...(r.relicPickupFloors || {}), [item.id]: (r.floorIdx || 0) + 1 },
    }));
    setPendingShop(s => ({ ...s, relics: s.relics.map((c,i) => i===idx ? null : c) }));
  }, [pendingShop, run]);

  const useRemoveService = useCallback(() => {
    if (!pendingShop || pendingShop.removeUsed) return;
    if (run.player.gold < pendingShop.removePrice) { setFloatingText('NOT ENOUGH GOLD'); return; }
    setPendingRemove({ source: 'shop' });
    setScreen('cardremove');
  }, [pendingShop, run]);

  // v3.10 SHOP DEPTH: upgrade a card from your deck
  const useShopUpgrade = useCallback(() => {
    if (!pendingShop || pendingShop.upgradeUsed) return;
    if (run.player.gold < pendingShop.upgradePrice) { setFloatingText('NOT ENOUGH GOLD'); return; }
    // check if there's at least one upgradeable card
    const anyUpgradable = (run.player.deck || []).some(cid => canUpgrade(cid));
    if (!anyUpgradable) { setFloatingText('NO UPGRADEABLE CARDS'); return; }
    setPendingUpgrade({ source: 'shop' });
    setScreen('cardupgrade');
  }, [pendingShop, run]);

  // v3.10 SHOP DEPTH: grant a random blessing (common/rare — no legendaries)
  const useShopBlessing = useCallback(() => {
    if (!pendingShop || pendingShop.blessingUsed) return;
    if (run.player.gold < pendingShop.blessingPrice) { setFloatingText('NOT ENOUGH GOLD'); return; }
    const pool = Object.values(BLESSINGS).filter(b => b && !b.legendary);
    if (pool.length === 0) return;
    const rng = makeRng((pendingShop._seed || 'shop') + '-bless-' + (run.floorIdx || 0));
    const pick = pool[Math.floor(rng() * pool.length)];
    if (!pick) return;
    sfx.gold();
    try { sfx.legendaryChime && sfx.legendaryChime(); } catch (e) {}
    try { triggerBurst && triggerBurst('rainbow'); } catch (e) {}
    setRun(r => {
      if (!r) return r;
      const player = { ...r.player };
      const bMods = { ...(player._blessMods || {}) };
      pick.apply(bMods, player);
      player._blessMods = bMods;
      player._blessings = [ ...(player._blessings || []), pick.id ];
      player.gold = Math.max(0, (player.gold || 0) - pendingShop.blessingPrice);
      const blessingPickupFloors = { ...(r.blessingPickupFloors || {}), [pick.id]: (r.floorIdx || 0) + 1 };
      return { ...r, player, blessingPickupFloors };
    });
    setPendingShop(s => ({ ...s, blessingUsed: true, lastBlessingId: pick.id }));
    setFloatingText(`BLESSED: ${pick.name}`);
    (async () => {
      const tag = normalizeTag(playerName);
      if (tag) { try { await patchProfile(tag, { blessing_picked: pick.id }); } catch (e) {} }
    })();
  }, [pendingShop, run, triggerBurst, playerName]);

  // v2.3 QoL: shop reroll — spend gold to reroll all remaining cards/relics.
  const rerollShop = useCallback(() => {
    if (!pendingShop) return;
    const cost = (pendingShop.rerollBasePrice || 40) + (pendingShop.rerollsUsed || 0) * 25;
    if (run.player.gold < cost) { setFloatingText('NOT ENOUGH GOLD'); return; }
    sfx.click && sfx.click();
    const rng = makeRng((pendingShop._seed || 'shop') + '-r' + (pendingShop.rerollsUsed || 0));
    const items = pickN(rng, Object.keys(CARDS).filter(k => !k.endsWith('+') && !['yap','cope'].includes(k)), 4);
    const relicsAvail = pickN(rng, ALL_RELIC_IDS.filter(r => !run.player.relics.includes(r)), 1);
    const shopMult = pendingShop._shopMult || 1;
    setRun(r => ({ ...r, player: { ...r.player, gold: r.player.gold - cost } }));
    setPendingShop(s => ({
      ...s,
      cards: items.map(id => ({ id, price: Math.floor((30 + rng()*40) * shopMult) })),
      relics: relicsAvail.map(id => ({ id, price: Math.floor((120 + rng()*60) * shopMult) })),
      rerollsUsed: (s.rerollsUsed || 0) + 1,
    }));
  }, [pendingShop, run]);

  const confirmRemoveCard = useCallback((deckIdx) => {
    if (!pendingRemove || !run) return;
    sfx.click();
    const price = pendingShop?.removePrice || 0;
    setRun(r => ({
      ...r,
      player: {
        ...r.player,
        gold: r.player.gold - price,
        deck: r.player.deck.filter((_, i) => i !== deckIdx),
      },
    }));
    setPendingShop(s => s ? ({ ...s, removeUsed: true }) : s);
    setPendingRemove(null);
    setScreen('shop');
  }, [pendingRemove, pendingShop, run]);

  const cancelRemoveCard = useCallback(() => {
    setPendingRemove(null);
    setScreen(pendingRemove?.source === 'shop' ? 'shop' : 'map');
  }, [pendingRemove]);

  const leaveShop = useCallback(() => {
    if (!pendingShop) return;
    setRun(r => ({ ...r, floorIdx: pendingShop.floorIdx + 1 }));
    setPendingShop(null);
    setScreen('map');
  }, [pendingShop]);

  const openRestUpgrade = useCallback(() => {
    if (!pendingRest) return;
    if (run?.player?.mods?.restNoUpgrade) { setFloatingText('BLOCKED BY CURSE'); return; }
    setPendingUpgrade({ source: 'rest' });
    setScreen('cardupgrade');
  }, [pendingRest, run]);

  const doRestHeal = useCallback(() => {
    if (!pendingRest) return;
    sfx.heal();
    setRun(r => {
      // v3.3 PERK: DEEP SLEEP — +N HP on top of base rest heal
      const bonus = r?.player?._perkRestBonus || 0;
      return { ...r, player: { ...r.player, hp: Math.min(r.player.maxHp, r.player.hp + Math.floor(r.player.maxHp * 0.3) + bonus) }, floorIdx: pendingRest.floorIdx + 1 };
    });
    setPendingRest(null);
    setScreen('map');
  }, [pendingRest]);

  const confirmUpgradeCard = useCallback((deckIdx) => {
    if (!run) return;
    const cardId = run.player.deck[deckIdx];
    if (!canUpgrade(cardId)) { setFloatingText('CAN\'T UPGRADE THIS'); return; }
    // If card has multiple upgrade branches, prompt player
    const branches = upgradeBranches(cardId);
    if (branches.length > 1) {
      setPendingBranchPick({ deckIdx, baseId: cardId });
      setScreen('branchpick');
      return;
    }
    sfx.levelup();
    const upgraded = upgradedId(cardId);
    // v2.8 QoL: Card Discovery — mark UPGRADED
    try { bumpCardStat(cardId, 'upgraded'); } catch (e) {}
    const newDeck = [...run.player.deck];
    newDeck[deckIdx] = upgraded;
    // v3.10 SHOP DEPTH: if source is 'shop', deduct gold and return to shop instead of map
    const fromShop = pendingUpgrade?.source === 'shop';
    if (fromShop && pendingShop) {
      const cost = pendingShop.upgradePrice;
      setRun(r => ({ ...r, player: { ...r.player, deck: newDeck, gold: Math.max(0, (r.player.gold || 0) - cost) } }));
      setPendingShop(s => ({ ...s, upgradeUsed: true }));
      setPendingUpgrade(null);
      setFloatingText(`UPGRADED: ${CARDS[upgraded]?.name || upgraded}`);
      setScreen('shop');
      return;
    }
    setRun(r => ({ ...r, player: { ...r.player, deck: newDeck }, floorIdx: (pendingRest?.floorIdx ?? r.floorIdx) + (pendingRest ? 1 : 0) }));
    setPendingUpgrade(null);
    if (pendingRest) setPendingRest(null);
    setScreen('map');
  }, [run, pendingRest, pendingUpgrade, pendingShop]);

  const cancelUpgradeCard = useCallback(() => {
    const fromShop = pendingUpgrade?.source === 'shop';
    setPendingUpgrade(null);
    setScreen(fromShop ? 'shop' : 'rest');
  }, [pendingUpgrade]);

  const chooseEventOption = useCallback((optIdx) => {
    if (!pendingEvent) return;
    const runCopy = { ...run, player: { ...run.player, deck: [...run.player.deck], relics: [...run.player.relics] } };
    pendingEvent.event.options[optIdx].act(runCopy);
    sfx.click();
    setRun({ ...runCopy, floorIdx: pendingEvent.floorIdx + 1 });
    setPendingEvent(null);
    setScreen('map');
  }, [pendingEvent, run]);

  // Shrine: sacrifice a card (or hp) for a permanent buff this run.
  const chooseShrineOption = useCallback((cardIdxToSacrifice) => {
    if (!pendingShrine || !run) return;
    const shrine = pendingShrine.shrine;
    const rng = makeRng(run.seed + '-shrine-' + pendingShrine.floorIdx);
    const runCopy = { ...run, player: { ...run.player, deck: [...run.player.deck], relics: [...run.player.relics] } };
    if (shrine.cost === 'card' && cardIdxToSacrifice != null) {
      runCopy.player.deck.splice(cardIdxToSacrifice, 1);
    } else if (shrine.cost === 'hp10') {
      runCopy.player.hp = Math.max(1, runCopy.player.hp - 10);
    }
    shrine.apply(runCopy, rng, ALL_RELIC_IDS);
    // Random upgrades from shrine of ascension
    if (runCopy.player._upgradeRandom) {
      const times = runCopy.player._upgradeRandom;
      for (let i = 0; i < times; i++) {
        const idxs = runCopy.player.deck.map((cid, j) => canUpgrade(cid) ? j : -1).filter(j => j >= 0);
        if (idxs.length) {
          const pick = idxs[Math.floor(rng() * idxs.length)];
          runCopy.player.deck[pick] = upgradedId(runCopy.player.deck[pick]);
        }
      }
      runCopy.player._upgradeRandom = 0;
    }
    sfx.buff();
    setRun({ ...runCopy, floorIdx: pendingShrine.floorIdx + 1 });
    setPendingShrine(null);
    setScreen('map');
  }, [pendingShrine, run]);

  const skipShrine = useCallback(() => {
    if (!pendingShrine) return;
    setRun(r => ({ ...r, floorIdx: pendingShrine.floorIdx + 1 }));
    setPendingShrine(null);
    setScreen('map');
  }, [pendingShrine]);

  // Treasure: pick a chest -> rolls outcome. Pass null to close/continue after result.
  const openTreasure = useCallback((chestIdx) => {
    if (!pendingTreasure || !run) return null;
    if (chestIdx === null) {
      setRun(r => ({ ...r, floorIdx: pendingTreasure.floorIdx + 1 }));
      setPendingTreasure(null);
      setScreen('map');
      return null;
    }
    const rng = makeRng(run.seed + '-treasure-' + pendingTreasure.floorIdx + '-' + chestIdx);
    const outcome = TREASURE_OUTCOMES[chestIdx];
    const runCopy = { ...run, player: { ...run.player, deck: [...run.player.deck], relics: [...run.player.relics] } };
    const msg = outcome.roll(runCopy, rng, ALL_RELIC_IDS);
    sfx.gold();
    setRun(runCopy);
    return msg;
  }, [pendingTreasure, run]);

  // Branch upgrade support
  const choosePendingBranch = useCallback((chosenId) => {
    if (!pendingBranchPick || !run) return;
    const { deckIdx } = pendingBranchPick;
    const newDeck = [...run.player.deck];
    newDeck[deckIdx] = chosenId;
    sfx.levelup();
    setRun(r => ({ ...r, player: { ...r.player, deck: newDeck }, floorIdx: (pendingRest?.floorIdx ?? r.floorIdx) + (pendingRest ? 1 : 0) }));
    setPendingBranchPick(null);
    setPendingUpgrade(null);
    if (pendingRest) setPendingRest(null);
    setScreen('map');
  }, [pendingBranchPick, run, pendingRest]);

  const cancelBranchPick = useCallback(() => {
    setPendingBranchPick(null);
    setScreen(pendingRest ? 'rest' : 'cardupgrade');
  }, [pendingRest]);

  const submitFinalScore = useCallback(async ({ name }) => {
    if (!lastRunSummary) return null;
    const tag = normalizeTag(name || playerName || 'ANON_GOON');
    setPlayerName(tag);
    // v3.14 GHOST MODE: persist this seed's best-score/floor for future replays.
    try {
      saveGhostIfBest(lastRunSummary.seed, {
        score: lastRunSummary.score,
        floor: lastRunSummary.floor,
        classId: lastRunSummary.player?.classId || null,
      });
    } catch (e) { /* localStorage disabled */ }
    // Build submission payload
    const payload = {
      player_name: tag,
      score: lastRunSummary.score,
      floor_reached: lastRunSummary.floor,
      boss_defeated: lastRunSummary.bossDefeated,
      seed: lastRunSummary.seed,
      cards_final: lastRunSummary.player.deck,
      relics: lastRunSummary.player.relics,
      is_daily: !!lastRunSummary.isDaily,
      daily_date: lastRunSummary.dailyDate || null,
      weekly_key: currentWeekKey(),
      class_id: lastRunSummary.player.classId || null,
      ascension: lastRunSummary.ascension || 0,
      curse_id: lastRunSummary.curse?.curse_id || null,
      best_streak: bestStreak || 0,
      avatar: profile?.avatar ? { ...profile.avatar, frame: profile.equipped_frame || null } : null,
    };
    let created = null;
    try {
      const axios = (await import('axios')).default;
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leaderboard`, payload);
      created = r.data;
    } catch (e) { /* fail silent */ }
    // Compute tokens earned
    let tokens = Math.floor(lastRunSummary.score / 20);
    if (lastRunSummary.bossDefeated) tokens += 100;
    // v3.10 STACKED CURSES: token multiplier grows with the size of the curse stack.
    // 1c = 2x, 2c = 2.5x, 3c = 3x, etc. Falls back to 1x if no curses were active.
    const curseStackCount = (lastRunSummary.curseStack?.length) || (lastRunSummary.curse ? 1 : 0);
    if (curseStackCount > 0) {
      tokens = Math.floor(tokens * stackedRunTokenMult(curseStackCount));
    }
    // v1.1 COLORPILL: streak bonus tokens on victory
    if (lastRunSummary.bossDefeated && lastRunSummary.streakBonus) {
      tokens += lastRunSummary.streakBonus;
    }
    // titles earned — v3.20 FIX: only credit tokens/titles for achievements the profile did NOT already own.
    const alreadyOwned = new Set(profile?.achievements || []);
    const titles = [];
    const freshAchievements = [];
    for (const aid of runAchievements) {
      if (alreadyOwned.has(aid)) continue;
      freshAchievements.push(aid);
      const a = ACHIEVEMENTS[aid];
      if (a?.title) titles.push(a.title);
      if (a?.tokens) tokens += a.tokens;
    }
    // v3.5 PERK: GALAXY GOOMER — +25% tokens
    const _tokenMult = lastRunSummary.player._perkTokenMult || 1;
    if (_tokenMult !== 1) tokens = Math.floor(tokens * _tokenMult);
    // ascension unlock
    let ascUnlock = null;
    if (lastRunSummary.bossDefeated) {
      const nextLvl = Math.min(5, (lastRunSummary.ascension || 0) + 1);
      ascUnlock = { class_id: lastRunSummary.player.classId, ascension_level: nextLvl };
    }
    // profile patch
    const patch = {
      slop_tokens_delta: tokens,
      total_wins_delta: lastRunSummary.bossDefeated ? 1 : 0,
      // v3.3 TALENT TREE: +1 perk point per boss defeated
      perk_points_delta: lastRunSummary.bossDefeated ? 1 : 0,
      score: lastRunSummary.score,
      floor: lastRunSummary.floor,
      achievements_earned: freshAchievements,
      titles_earned: titles,
      ascension_unlock: ascUnlock,
      mastery_gain: lastRunSummary.bossDefeated && lastRunSummary.player.classId
        ? { class_id: lastRunSummary.player.classId, delta: 1 }
        : null,
    };
    if (tag) {
      patchProfile(tag, patch).then(p => {
        if (p) {
          setProfile(p);
          // v0.9.5 mastery-5/10 achievements
          const highest = Math.max(0, ...Object.values(p.masteries || {}));
          if (highest >= 10) {
            patchProfile(tag, { achievements_earned: ['mastery_10','mastery_5'] });
          } else if (highest >= 5) {
            patchProfile(tag, { achievements_earned: ['mastery_5'] });
          }
        }
      });
    }
    return { created, tokens };
  }, [lastRunSummary, runAchievements, playerName, setPlayerName, bestStreak]);

  const curseThisRun = useCallback(async (runCode, curseId) => {
    if (!runCode || !curseId) return null;
    const tag = normalizeTag(playerName);
    const r = await createCurse({
      run_code: runCode, curse_id: curseId, cursed_by: tag,
      original_score: lastRunSummary?.score || 0,
    });
    sfx.buff();
    return r;
  }, [playerName, lastRunSummary]);

  const goToMenu = useCallback(() => {
    stopMusic();
    setRun(null); setCombat(null); setPendingReward(null); setPendingShop(null); setPendingRest(null); setPendingEvent(null); setLastRunSummary(null);
    setPendingUpgrade(null); setPendingRemove(null); setPendingClassChoice(null);
    setPendingBoon(null); setPendingShrine(null); setPendingTreasure(null); setPendingBranchPick(null);
    setActiveCurse(null); setSeedGhosts([]); setRunAchievements([]); setAchievementToasts([]);
    setScreen('menu');
    // Refresh profile
    refreshProfile();
  }, [refreshProfile]);

  const toggleMute = useCallback(() => {
    const m = !muted;
    setMutedState(m);
    setMuted(m);
  }, [muted]);

  const toggleStreamerMode = useCallback(() => {
    setStreamerMode(prev => {
      const next = !prev;
      try { localStorage.setItem('friendslop.streamer', next ? '1' : '0'); } catch (e) {}
      return next;
    });
  }, []);

  // ---- v0.9.5 Twitch chat integration ----
  const resetChatVotes = useCallback(() => { setChatVotes({}); setChatVoteWinner(null); }, []);

  const connectTwitch = useCallback(async (channel) => {
    try { localStorage.setItem('friendslop.twitchChannel', channel || ''); } catch (e) {}
    setTwitchChannel(channel);
    resetChatVotes();
    const res = await connectAnonymousChat(channel, (choice, voter) => {
      setChatVotes(prev => {
        // one vote per voter across all choices for the current tally
        const next = { ...prev };
        for (const k of Object.keys(next)) next[k] = new Set(next[k]);
        for (const k of Object.keys(next)) next[k].delete(voter);
        if (!next[choice]) next[choice] = new Set();
        next[choice].add(voter);
        return next;
      });
    });
    setTwitchStatus({ connected: !!res.ok, channel: res.channel || null, error: res.error });
    return res;
  }, [resetChatVotes]);

  const disconnectTwitch = useCallback(async () => {
    await disconnectTwitchChat();
    setTwitchStatus({ connected: false, channel: null });
    resetChatVotes();
  }, [resetChatVotes]);

  // v0.9.5: prestige a class — resets mastery, awards permanent bonus
  const prestigeClass = useCallback(async (classId, bonusType) => {
    const tag = normalizeTag(playerName);
    if (!tag) return;
    const p = await patchProfile(tag, { prestige: { class_id: classId, bonus_type: bonusType } });
    if (p) setProfile(p);
  }, [playerName]);

  // v2.0 AVATARS + v2.1 DRAWING MODE
  const updateAvatar = useCallback(async ({ emoji, color, title, drawing }) => {
    const tag = normalizeTag(playerName);
    if (!tag) return null;
    // Pass drawing through explicitly (null clears it on the backend).
    const payload = { emoji, color, title };
    if (drawing !== undefined) payload.drawing = drawing;
    const p = await patchProfile(tag, { avatar: payload });
    if (p) setProfile(p);
    return p;
  }, [playerName]);

  // ---- Draft ----
  const beginClassSelectWithDraft = useCallback((classId, seed) => {
    const s = (seed || genSeed()).toUpperCase();
    setPendingClassChoice({ seed: s, daily: null, runCode: null, forcedClassId: classId, useDraft: true });
    setActiveCurse(null);
    setScreen('classselect');
  }, []);

  // ---- Party ----
  const hostSeedAndStart = useCallback(async (party) => {
    setPartyState(party);
    beginClassSelect(party.seed, null, null);
  }, [beginClassSelect]);

  const joinPartyAndStartRun = useCallback(async (party) => {
    setPartyState(party);
    beginClassSelect(party.seed, null, null);
  }, [beginClassSelect]);

  // Sync party progress every time floor/screen changes during a run
  useEffect(() => {
    if (!partyState || !run) return;
    const tag = normalizeTag(playerName);
    if (!tag) return;
    const status = lastRunSummary
      ? (lastRunSummary.bossDefeated ? 'won' : lastRunSummary.victory === false ? 'lost' : 'playing')
      : 'playing';
    // v2.2 PvP RACE: also push hp_pct + boss_defeated + avatar for the race HUD
    const hp = run.player?.hp ?? 0;
    const maxHp = run.player?.maxHp ?? 60;
    const hp_pct = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
    // v2.7 PvP LIVE SCORE: compute a running score so the race HUD reflects who's ahead mid-run.
    // Formula: floor*100 + hp_pct*50 + gold + kill_credits + streak. Falls back to submitted score at end.
    let liveScore = lastRunSummary?.score || 0;
    if (!lastRunSummary) {
      const stats = run._runStats || {};
      const floorScore = (run.floorIdx || 0) * 100;
      const hpBonus = Math.floor(hp_pct * 50);
      const goldBonus = Math.floor((run.player?.gold || 0) * 0.5);
      const dmgBonus = Math.floor((stats.dmgDealt || 0) * 0.25);
      const killBonus = (stats.kills || 0) * 15;
      const streakBonus = (run.streakThisRun || 0) * 10;
      liveScore = floorScore + hpBonus + goldBonus + dmgBonus + killBonus + streakBonus;
    }
    partyUpdateApi(partyState.room_code, tag, liveScore, run.floorIdx + 1, status, run.player.classId, {
      hp_pct,
      boss_defeated: !!(lastRunSummary?.bossDefeated),
      avatar: profile?.avatar ? { ...profile.avatar, frame: profile.equipped_frame || null } : undefined,
    });
  }, [run && run.floorIdx, run && run.player && run.player.hp, run && run.player && run.player.gold, run && run._runStats && run._runStats.dmgDealt, lastRunSummary, partyState, playerName, profile]);

  // v2.2 PvP RACE: poll party state every 5s during a party run so opponents' HUD updates live.
  useEffect(() => {
    if (!partyState || !run) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const axios = (await import('axios')).default;
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const r = await axios.get(`${API}/party/${encodeURIComponent(partyState.room_code)}`);
        if (!cancelled && r.data) setPartyState(r.data);
      } catch (e) {}
    };
    const id = setInterval(tick, 5000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [partyState?.room_code, run?.seed]);

  // v1.0: SAVE / CONTINUE RUN — persist run state to localStorage so a closed tab can resume.
  const [hasSavedRun, setHasSavedRun] = useState(() => {
    try { return !!localStorage.getItem('friendslop.save'); } catch (e) { return false; }
  });
  const prevRunRef = useRef(null);

  // v1.1 COLORPILL: per-act body class + v1.3 act-shifting music
  useEffect(() => {
    const cls = ['act-1','act-2','act-3'];
    for (const c of cls) document.body.classList.remove(c);
    if (run?.floorIdx != null) {
      const f = run.floorIdx + 1;
      const actNum = f < 11 ? 1 : f < 16 ? 2 : 3;
      document.body.classList.add(`act-${actNum}`);
      setMusicAct(actNum);
    } else {
      setMusicAct(1);
    }
    return () => { for (const c of cls) document.body.classList.remove(c); };
  }, [run?.floorIdx]);

  useEffect(() => {
    const hadRun = prevRunRef.current;
    prevRunRef.current = run;
    // Only clear the saved run when a real run transitions to null/ended (never on initial mount)
    if (hadRun && (!run || run.ended)) {
      try { localStorage.removeItem('friendslop.save'); } catch (e) {}
      setHasSavedRun(false);
      return;
    }
    // Persist during safe screens
    if (run && !run.ended && (screen === 'map' || screen === 'menu' || screen === 'gameover')) {
      try {
        const snapshot = { run, screen, tag: playerName, savedAt: Date.now() };
        localStorage.setItem('friendslop.save', JSON.stringify(snapshot));
        setHasSavedRun(true);
        // v2.4 QoL #6: fire an auto-save toast (once per floor change on map)
        if (screen === 'map') setLastAutoSaveAt(Date.now());
      } catch (e) {}
    }
  }, [run, screen, playerName]);

  const continueSavedRun = useCallback(() => {
    try {
      const raw = localStorage.getItem('friendslop.save');
      if (!raw) return false;
      const snap = JSON.parse(raw);
      if (!snap?.run) return false;
      setPlayerName(snap.tag || 'ANON_GOON');
      setRun(snap.run);
      startMusic();
      setScreen('map');
      return true;
    } catch (e) { return false; }
  }, []);

  const discardSavedRun = useCallback(() => {
    try { localStorage.removeItem('friendslop.save'); } catch (e) {}
    setHasSavedRun(false);
  }, []);

  // v2.1 DEV HELPER — jump the current run to just before midboss (floor 10) for QA of blessing flow.
  // Exposed on window in non-production only. Usage: window.__slop_jumpToMidboss()
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    window.__slop_jumpToMidboss = () => {
      setRun(r => {
        if (!r) { console.warn('[slop] no run active — start one first'); return r; }
        // Move to floor 10 (index 9) and put player at full hp for test
        return { ...r, floorIdx: 9, player: { ...r.player, hp: r.player.maxHp } };
      });
      console.log('[slop] jumped to floor 10 (midboss). enter the midboss node.');
    };
    // v2.2 dev helper: jump to floor 2 (typical first elite) for QA of skip-for-curse
    window.__slop_jumpToElite = () => {
      setRun(r => {
        if (!r) { console.warn('[slop] no run active — start one first'); return r; }
        return { ...r, floorIdx: 1, player: { ...r.player, hp: r.player.maxHp } };
      });
      console.log('[slop] jumped to floor 2 (elite typically here). enter the elite node.');
    };
    // v2.3 dev helper: force the blessing pick screen with a rarity-mixed offer set for UI QA
    window.__slop_forceBlessing = () => {
      const offers = [
        BLESSINGS.bless_ascension,      // legendary
        BLESSINGS.bless_strength,        // rare
        BLESSINGS.bless_block,           // common
      ].filter(Boolean);
      setPendingBlessing({
        offers,
        floorIdx: 9,
        choiceIdx: 0,
        pendingReward: { cards: [], gold: 0, floorIdx: 9, choiceIdx: 0 },
      });
      setScreen('blessing');
      console.log('[slop] forced blessing pick with 3 offers (legendary/rare/common).');
    };
    // v2.4 dev helper: force the post-combat recap screen for UI QA
    window.__slop_forceRecap = () => {
      setPendingRecap({
        kind: 'monster', floor: 5, enemyName: 'DEV DUMMY', enemySprite: '👾',
        turns: 4, cardsPlayed: 9, dmgDealt: 87, dmgTaken: 12, biggestHit: 34, exhausted: 2,
        finalHp: 45, maxHp: 60, gold: 32, rewardPreviewCount: 3,
        nextScreen: 'map',
      });
      setScreen('recap');
      console.log('[slop] forced combat recap screen.');
    };
    // v3.9 QA helper: force a combat of a given kind (monster|elite|midboss|boss) with a class.
    // Bypasses the map so the enemy-intro card / final-blow cinematic can be verified quickly.
    window.__slop_forceCombat = (kind = 'elite', classId = 'sigma', opts = {}) => {
      const cls = classById(classId) || classById('sigma');
      const seed = 'DEVQA-' + Date.now();
      const rng = makeRng(seed);
      const player = newPlayer(seed, cls, {});
      let enemy;
      if (kind === 'boss') enemy = getFinalBoss(rng, seed);
      else if (kind === 'midboss') enemy = getMidBoss(rng);
      else if (kind === 'elite') enemy = pickEliteForFloor(rng, 5, 0);
      else enemy = pickEnemyForFloor(rng, 3);
      const c = startCombat({ ...player, _runStreak: 0 }, enemy, seed + '-cmb', { blockPerTurn: enemy.blockPerTurn });
      if (typeof opts.enemyHp === 'number') { c.enemy.hp = opts.enemyHp; }
      setRun({ seed, player, floorIdx: 5, map: [], ascension: 0, gold: 0 });
      setCombat({ state: c, floorIdx: 5, choiceIdx: 0, kind: kind === 'midboss' ? 'midboss' : kind });
      setScreen('combat');
      console.log('[slop] forced combat', kind, classId, '→', enemy.name, 'hp=', c.enemy.hp);
    };
    return () => { try { delete window.__slop_jumpToMidboss; delete window.__slop_jumpToElite; delete window.__slop_forceBlessing; delete window.__slop_forceRecap; delete window.__slop_forceCombat; } catch (e) {} };
  }, []);


  const value = {
    screen, setScreen,
    leaderboardInitialTab, setLeaderboardInitialTab,
    playerName, setPlayerName,
    profile, refreshProfile,
    selectedClass, pendingClassChoice, activeCurse, selectedAscension, setSelectedAscension,
    run, combat, pendingReward, pendingShop, pendingEvent, pendingRest, pendingUpgrade, pendingRemove,
    pendingBoon, pendingShrine, pendingTreasure, pendingBranchPick,
    pendingBlessing, takeBlessing,
    pendingEliteChoice, eliteFight, eliteSkip,
    pendingRecap, dismissRecap,
    lastAutoSaveAt,
    lastRunSummary, floatingText, setFloatingText,
    muted, toggleMute,
    streamerMode, toggleStreamerMode,
    // v0.9.5 Twitch chat vote
    twitchChannel, setTwitchChannel, twitchStatus,
    chatVotes, chatVoteWinner,
    connectTwitch, disconnectTwitch, resetChatVotes,
    prestigeClass,
    updateAvatar,
    // v1.0
    hasSavedRun, continueSavedRun, discardSavedRun,
    recordFloorEvent,
    seedGhosts,
    // v1.1 COLORPILL
    runStreak, bestStreak, triggerBurst,
    achievementToasts, runAchievements,
    draftDeck, setDraftDeck, beginClassSelectWithDraft,
    partyState, setPartyState, hostSeedAndStart, joinPartyAndStartRun,
    startQuickRun, beginClassSelect, confirmClass, startWeeklyRun,
    enterNode, doPlayCard, doEndTurn, undoLastCard, canUndo,
    takeRewardCard, skipReward,
    buyCard, buyRelic, leaveShop,
    useRemoveService, confirmRemoveCard, cancelRemoveCard,
    useShopUpgrade, useShopBlessing,
    rerollShop,
    doRestHeal, openRestUpgrade,
    confirmUpgradeCard, cancelUpgradeCard,
    chooseEventOption,
    chooseBoon, chooseShrineOption, skipShrine, openTreasure,
    choosePendingBranch, cancelBranchPick,
    submitFinalScore, curseThisRun,
    ghostRun,
    goToMenu,
  };
  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

function computeScore(player, floor, victory, ascension = 0) {
  let s = floor * 100;
  s += player.hp * 2;
  s += player.gold;
  s += player.relics.length * 40;
  s += player.deck.length * 3;
  if (victory) s += 2500;
  const ascBonus = 1 + (ascension * 0.15);
  return Math.floor(s * ascBonus);
}
